import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import Button from '../../../util_components/button';
import CustomLoading from '../../../util_components/custom_loading';
import RequiredReasonTextarea from '../../../util_components/required_reason_textarea';
import { CareRequestFields, CareSaveRetry } from '../../../util_components/care_request';
import { FormActions } from '../../../util_components/form_layout';
import {
  KIND_RESCHEDULE,
  build_create_payload,
  care_api_error,
  covers_care
} from '../../../util_components/care_request/care_request_rules';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { get_time_interval_for_duration } from '../../../../utils/date_time';
import useCareCapture from './use_care_capture';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import usePayTimeWarning from '../../../util_components/pay_time_warning/use_pay_time_warning';
import { with_pay_time_ack } from '../../../util_components/pay_time_warning/pay_time_warning_rules';
import { plan_time_change } from './session_change_rules';

const FMT = 'YYYY-MM-DD HH:mm:ss';

// The session page's "Change session time" body; mounted per open, starting at the session's start in the page zone.
function ChangeTimeForm(props) {
  const { session_uuid, session_details, iana_timezone } = props;
  const [new_time, setNewTime] = useState(() =>
    moment(moment.tz(session_details.epoch_start_time, iana_timezone).format(FMT)).toDate()
  );
  const [reason, setReason] = useState('');
  const [reason_error, setReasonError] = useState(false);
  const [loading, setLoading] = useState(false);
  const pw = usePayTimeWarning();
  const care = useCareCapture({
    session_uuid,
    kind: covers_care(KIND_RESCHEDULE, session_details.session_type) ? KIND_RESCHEDULE : null,
    on_saved: props.on_care_saved,
    on_retry_change: props.on_retry_change
  });

  // ack: the pay-time acknowledgement from the warning step (A-52); null on the first send
  const change_time = async (ack = null) => {
    const plan = plan_time_change({
      time_change_reason: reason,
      care_draft: care.draft,
      now_ms: Date.now()
    });
    if (plan.reason_error) {
      setReasonError(true);
      return;
    }
    if (plan.care_errors) {
      care.setErrors(plan.care_errors);
      return;
    }
    const epoch_time = moment.tz(moment(new_time).format(FMT), iana_timezone).valueOf();
    setLoading(true);
    let screenshot_key = '';
    try {
      screenshot_key = await care.upload_once();
    } catch (e) {
      setLoading(false);
      props.notify(
        'error',
        care_api_error(e, 'The screenshot could not be uploaded. Nothing was changed.').message
      );
      return;
    }
    const payload = with_pay_time_ack(
      {
        session_uuid: session_details.session_uuid,
        start_time: epoch_time,
        teacher_uuid: session_details.teacher_uuid,
        student_uuid: session_details.student_uuid,
        reason: plan.reason
      },
      ack
    );
    post_api(get_api_url('update_session_time'), payload, true)
      .then(() => {
        pw.clear();
        if (!care.draft) {
          props.on_done();
          return;
        }
        // loading stays on (Confirm disabled) until the Care save settles: success via on_done, failure via Retry
        care.save_after_change(
          build_create_payload(care.draft, {
            session_uuid: session_details.session_uuid,
            note: plan.reason,
            screenshot_key,
            expected_start_time: moment(epoch_time).toISOString()
          }),
          props.on_done
        );
      })
      .catch((e) => {
        if (pw.catch_warning(e)) {
          setLoading(false);
          return;
        }
        pw.clear();
        let err_message;
        try {
          err_message = e.response.data.message;
        } catch (err) {
          err_message = 'Some error occured. Please contact dev team.';
        }
        props.on_failed(err_message);
      });
  };

  const old_label = moment(session_details.epoch_start_time).tz(iana_timezone).format(FMT);
  if (pw.warning) {
    return (
      <div className="min-w-0 overflow-visible">
        <h3>Change session time</h3>
        <PayTimeWarningStep
          warning={pw.warning}
          busy={loading}
          changeLabel="Change time"
          onAcknowledge={(ack) => change_time(ack)}
          onChange={pw.clear}
        />
      </div>
    );
  }
  return (
    <div className="min-w-0 overflow-visible">
      {/* once a Care save fails, the Retry dialog owns the wait, not this overlay */}
      {loading && !care.retry ? <CustomLoading /> : null}
      <h3>Change session time</h3>
      <div className="myt-field">
        <label className="myt-form-label" htmlFor="change-time-picker">
          {`New class time (${moment().tz(iana_timezone).format('z')})`}
        </label>
        <DatePicker
          id="change-time-picker"
          selected={new_time}
          onChange={(date) => setNewTime(date)}
          minDate={new Date()}
          maxDate={moment(new Date()).add(30, 'days').toDate()}
          showTimeSelect
          timeIntervals={get_time_interval_for_duration(session_details.duration)}
          dateFormat="MMMM d, yyyy h:mm aa"
          popperPlacement="bottom-start"
          popperProps={{ strategy: 'fixed' }}
        />
      </div>
      <RequiredReasonTextarea
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          setReasonError(false);
        }}
        placeholder="Why is the session time being changed? (required)"
        error={reason_error}
        errorText="A note is required (at least 3 characters) to change the session time"
        height="80px"
      />
      {care.draft ? (
        <CareRequestFields draft={care.draft} errors={care.errors} onChange={care.patch} />
      ) : null}
      {care.retry ? (
        <CareSaveRetry
          message={care.retry.message}
          busy={care.retry.busy}
          warning={care.retry.warning}
          requestedAt={care.retry.payload.requested_at}
          onRetry={care.retry_save}
          onClose={props.on_close}
        />
      ) : (
        <FormActions>
          <Button
            disabled={old_label === moment(new_time).format(FMT) || loading}
            onClick={() => change_time()}
          >
            {loading ? 'Confirming…' : 'Confirm'}
          </Button>
        </FormActions>
      )}
    </div>
  );
}

ChangeTimeForm.propTypes = {
  session_uuid: PropTypes.string,
  session_details: PropTypes.object.isRequired,
  iana_timezone: PropTypes.string,
  on_done: PropTypes.func.isRequired,
  on_failed: PropTypes.func.isRequired,
  on_close: PropTypes.func.isRequired,
  on_care_saved: PropTypes.func.isRequired,
  on_retry_change: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired
};

ChangeTimeForm.defaultProps = {
  session_uuid: '',
  iana_timezone: undefined
};

export default ChangeTimeForm;
